// The API contract ("port") of the shared client-file areas. Shared code cannot import a generated
// api-client (each app owns its own — see auth/authMiddleware.ts), so the contract is stated here
// app-neutrally and each app binds its own generated client to it in its clientFileApiAdapter.ts —
// an assignment the compiler re-checks on every build.
//
// The backend MODELS are not hand-written: they are emitted into ./api-client/clientFileApiModels.gen.ts
// from the main app's generated client, validated against external-api.json so only portal-exposed
// models can become shared (refresh via `npm run generateApiClient` in either app). This file adds
// only what cannot be generated: the per-area slice interfaces, the frontend-only drag-and-drop
// seam, and the few internal-only models the external document does not carry.
//
// Slices are optional: an app provides a slice only when its backend surface supports it, and the
// portal enables an area's UI only when its slice is present.
import type { ComponentType } from "react";

import type { QueryClient } from "@tanstack/react-query";

import type { ApiInitOverride } from "../../api/handleErrorLocally";
import type {
  AssetAnswersDto,
  AssetDependantsResponse,
  AssetDocumentArea,
  AssetExpensesTabResponse,
  AssetFinancialHoldingResponse,
  AssetIncomeTabResponse,
  AssetInventoryCompletenessResponse,
  AssetInventoryDocumentResponse,
  AssetOwnClaimsTabResponse,
  AssetPersonalDataResponse,
  AssetsTabResponse,
  BankruptcyCapabilityResponse,
  BankruptcyChecklistResponse,
  ClaimResponse,
  ClientNoteResponse,
  CreateClaimRequest,
  CreateClientSpecificProceedingParticipantRequest,
  DocumentResponse,
  DonationAnswersDto,
  DonationsTabResponse,
  DropdownOptionResponse,
  ProceedingParticipantResponse,
  ProceedingParticipantType,
  PropertyRightAnswersDto,
  PropertyRightsTabResponse,
  SaveAssetDependantsRequest,
  SaveAssetExpensesRequest,
  SaveAssetIncomeRequest,
  SaveAssetOwnClaimsRequest,
  SaveAssetPersonalDataRequest,
  SaveAssetsRequest,
  SaveBankruptcyConsequencesRequest,
  SaveDangerousDebtsRequest,
  SaveDonationsRequest,
  SavePropertyRightsRequest,
  SaveSpecialDebtsRequest,
  SearchResultsOfProceedingParticipantSearchResult,
  SystemDropdownCategory,
  SystemDropdownOptionResponse,
  UpdateClaimRequest,
  UpdateClientNoteRequest,
  UpdateProceedingParticipantRequest,
} from "./api-client/clientFileApiModels.gen";

// Every generated model (and the RelationshipType/DropdownCategory const enums with their runtime
// values) stays importable from this module and from @shared.
export * from "./api-client/clientFileApiModels.gen";

// --- Neutral model names ------------------------------------------------------------------------
// The names the shared areas were lifted with, mapped onto the generated models. Aliases only —
// the definitions live in the generated file.

export type BankruptcyChecklist = BankruptcyChecklistResponse;
export type BankruptcyCapability = BankruptcyCapabilityResponse;
export type SaveDangerousDebts = SaveDangerousDebtsRequest;
export type SaveSpecialDebts = SaveSpecialDebtsRequest;
export type SaveBankruptcyConsequences = SaveBankruptcyConsequencesRequest;
export type AssetPersonalData = AssetPersonalDataResponse;
export type SaveAssetPersonalData = SaveAssetPersonalDataRequest;
export type AssetExpensesTab = AssetExpensesTabResponse;
export type SaveAssetExpenses = SaveAssetExpensesRequest;
export type ExpenseProof = AssetInventoryDocumentResponse;
export type AssetIncomeTab = AssetIncomeTabResponse;
export type SaveAssetIncome = SaveAssetIncomeRequest;
export type IncomeProof = AssetInventoryDocumentResponse;
export type AssetDependants = AssetDependantsResponse;
export type SaveAssetDependants = SaveAssetDependantsRequest;
export type AssetOwnClaimsTab = AssetOwnClaimsTabResponse;
export type SaveAssetOwnClaims = SaveAssetOwnClaimsRequest;
export type OwnClaimDocument = AssetInventoryDocumentResponse;
export type AssetsTab = AssetsTabResponse;
export type SaveAssets = SaveAssetsRequest;
export type AssetAnswers = AssetAnswersDto;
export type FinancialHolding = AssetFinancialHoldingResponse;
export type AssetDocument = AssetInventoryDocumentResponse;
export type PropertyRightsTab = PropertyRightsTabResponse;
export type SavePropertyRights = SavePropertyRightsRequest;
export type PropertyRightAnswers = PropertyRightAnswersDto;
export type DonationsTab = DonationsTabResponse;
export type SaveDonations = SaveDonationsRequest;
export type DonationAnswers = DonationAnswersDto;
export type AssetInventoryCompleteness = AssetInventoryCompletenessResponse;
export type BankStatement = AssetInventoryDocumentResponse;
export type BankStatementResponse = AssetInventoryDocumentResponse;
// Trimmed (via Pick, so renames still break loudly) to the fields the shared document blocks read:
// both generated document shapes (DocumentResponse and AssetInventoryDocumentResponse) satisfy it.
export type ClientDocument = Pick<DocumentResponse, "documentId" | "fileName" | "contentType" | "fileSizeBytes">;
export type Claim = ClaimResponse;
export type CreateClaim = CreateClaimRequest;
export type UpdateClaim = UpdateClaimRequest;
export type ProceedingParticipant = ProceedingParticipantResponse;
export type ProceedingParticipantSearchResults = SearchResultsOfProceedingParticipantSearchResult;
export type CreateClientSpecificProceedingParticipant = CreateClientSpecificProceedingParticipantRequest;
export type UpdateProceedingParticipant = UpdateProceedingParticipantRequest;
export type DropdownOption = DropdownOptionResponse;
export type ClientNote = ClientNoteResponse;
export type UpdateClientNote = UpdateClientNoteRequest;

// --- Internal-only models -----------------------------------------------------------------------
// Not part of the external OpenAPI document (staff-only endpoints), so they cannot be generated
// from it and are stated here. Used only by the optional slice members below that the portal omits.

/** Body of the staff-only asset-inventory document read toggle (see markAssetInventoryDocumentRead below). */
export interface MarkAssetInventoryDocumentRead {
  isRead: boolean;
}

/** The client the expenses can be shared with, found by case number (see getAssetInventoryExpensePartner below). */
export interface AssetExpensePartner {
  clientId: string;
  caseNumber: string;
  salutation?: string | null;
  firstName: string;
  lastName: string;
}

/**
 * Body of the staff-only bulk balance surcharge (Saldo aufschlagen — see
 * applyClaimBalanceSurcharge below): the selected claims, the percentage their balance is raised
 * by, the date the entries it writes carry, and the type they are recorded under.
 */
export interface ApplyClaimBalanceSurcharge {
  claimIds: string[];
  percentage: number;
  date: Date;
  entryType: string | null;
}

/** What the bulk balance surcharge did: how many claims it touched, and the totals either side. */
export interface ClaimBalanceSurcharge {
  affectedClaimCount: number;
  previousTotal: number;
  newTotal: number;
}

/**
 * Body of the staff-only bulk balance carry-forward (see carryForwardClaimBalances below): the
 * claims whose current balance is repeated in a new entry. The date that entry carries is the
 * server's, so it is not part of the request.
 */
export interface CarryForwardClaimBalances {
  claimIds: string[];
}

/**
 * What the bulk balance carry-forward did: the date the new entries carry, how many claims gained
 * one, and the selected claims left untouched because their balance already stands at that date or
 * beyond, or they have no balance history to repeat.
 */
export interface ClaimBalanceCarryForward {
  date: Date;
  affectedClaimCount: number;
  skippedClaimIds: string[];
}

export interface ViennaAddressSearchResult {
  street: string;
  houseNumber: string;
  zipCode: string;
}

export interface ViennaAddressSearchResults {
  data: ViennaAddressSearchResult[];
}

// --- Document drag-and-drop (internal app only) ------------------------------------------------

/**
 * The asset-inventory upload areas that double as drag-and-drop zones. The runtime values MUST
 * stay identical to the internal app's DocumentGroup enum members — they are used as droppable
 * ids and as move targets on the wire.
 */
export const AssetDocumentZone = {
  BankStatement: "AssetInventoryBankStatement",
  Dependants: "AssetInventoryDependants",
  Income: "AssetInventoryIncome",
  Expenses: "AssetInventoryExpenses",
  OwnClaims: "AssetInventoryOwnClaims",
  DomesticRealEstate: "AssetInventoryDomesticRealEstate",
  ForeignRealEstate: "AssetInventoryForeignRealEstate",
  BuildingSavingsContracts: "AssetInventoryBuildingSavingsContracts",
  SavingsAccounts: "AssetInventorySavingsAccounts",
  LifeInsurances: "AssetInventoryLifeInsurances",
  PensionInsurances: "AssetInventoryPensionInsurances",
  SecuritiesDepots: "AssetInventorySecuritiesDepots",
  CashAndBankAssets: "AssetInventoryCashAndBankAssets",
  Vehicles: "AssetInventoryVehicles",
  BuildingsOnForeignLand: "AssetInventoryBuildingsOnForeignLand",
  TenancyRights: "AssetInventoryTenancyRights",
  CompanyShares: "AssetInventoryCompanyShares",
  CooperativeMemberships: "AssetInventoryCooperativeMemberships",
  OtherPropertyRights: "AssetInventoryOtherPropertyRights",
  Donations: "AssetInventoryDonations",
} as const;
export type AssetDocumentZone = (typeof AssetDocumentZone)[keyof typeof AssetDocumentZone];

/**
 * A document move queued by drag-and-drop but not persisted yet — persisted on the owning form's
 * save, discarded on reset. Zone ids are opaque strings here (the internal app's full DocumentGroup
 * space); at least one of source/target is an AssetDocumentZone.
 */
export interface PendingDocumentMove {
  document: ClientDocument;
  source: string;
  target: string;
}

/**
 * Props of the injected upload-area section (drop target, saved rows with the app's file actions,
 * pending uploads, pending moves). Uploads and moves are deferred: they live in the owning view's
 * Formik state and are persisted on save, so the section only displays and mutates the fields via
 * the callbacks.
 */
export interface AssetDocumentSectionProps {
  clientId: string;
  zone: AssetDocumentZone;
  hint?: string;
  pendingFiles: File[];
  documentMoves: PendingDocumentMove[];
  onDropFiles: (files: File[]) => void;
  onRemovePending: (index: number) => void;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
  gap?: number;
  /** Leaves out the area title, for an upload area that sits right below the question it belongs to. */
  hideTitle?: boolean;
}

// --- Slices --------------------------------------------------------------------------------------

export interface BankruptcyChecklistSliceApi {
  getBankruptcyChecklist(params: { clientId: string }): Promise<BankruptcyChecklist>;
  saveBankruptcyChecklistDangerousDebts(params: {
    clientId: string;
    saveDangerousDebtsRequest: SaveDangerousDebts;
  }): Promise<BankruptcyChecklist>;
  saveBankruptcyChecklistSpecialDebts(params: {
    clientId: string;
    saveSpecialDebtsRequest: SaveSpecialDebts;
  }): Promise<BankruptcyChecklist>;
  saveBankruptcyChecklistBankruptcyConsequences(params: {
    clientId: string;
    saveBankruptcyConsequencesRequest: SaveBankruptcyConsequences;
  }): Promise<BankruptcyChecklist>;
}

/**
 * Cross-cutting lookup data the shared field components read (admin-maintained dropdown options,
 * system-relevant dropdown options, Vienna address search). `searchViennaAddresses` is optional
 * within the slice: an app without the Wiener-Adressregister search provides the slice without it,
 * and the shared street field falls back to a plain text field.
 */
export interface LookupsSliceApi {
  getAllDropdownOptions(): Promise<DropdownOption[]>;
  getSystemDropdownOptions(params: {
    category: SystemDropdownCategory;
    includeHidden?: boolean;
  }): Promise<SystemDropdownOptionResponse[]>;
  // Property-style (not a method) so it can be extracted and passed around unbound.
  searchViennaAddresses?: (params: { searchText: string }) => Promise<ViennaAddressSearchResults>;
}

export interface AssetInventorySliceApi {
  getClientAssetPersonalData(params: { clientId: string }): Promise<AssetPersonalData>;
  saveClientAssetPersonalData(params: {
    clientId: string;
    saveAssetPersonalDataRequest: SaveAssetPersonalData;
  }): Promise<AssetPersonalData>;
  getAssetInventoryExpenses(params: { clientId: string }): Promise<AssetExpensesTab>;
  saveAssetInventoryExpenses(params: {
    clientId: string;
    saveAssetExpensesRequest: SaveAssetExpenses;
  }): Promise<AssetExpensesTab>;
  // Optional: the expense-partner endpoints are staff-only (not exposed to the portal), so the external
  // client omits them. Only ever called behind the `counselorExpensePartner` capability (internal-only).
  getAssetInventoryExpensePartner?(
    params: { clientId: string; caseNumber: string },
    initOverrides?: RequestInit | ApiInitOverride
  ): Promise<AssetExpensePartner>;
  shareAssetInventoryExpensesWithPartner?(params: { clientId: string; partnerClientId: string }): Promise<void>;
  getAssetInventoryIncome(params: { clientId: string }): Promise<AssetIncomeTab>;
  saveAssetInventoryIncome(params: {
    clientId: string;
    saveAssetIncomeRequest: SaveAssetIncome;
  }): Promise<AssetIncomeTab>;
  getAssetInventoryDependants(params: { clientId: string }): Promise<AssetDependants>;
  saveAssetInventoryDependants(params: {
    clientId: string;
    saveAssetDependantsRequest: SaveAssetDependants;
  }): Promise<AssetDependants>;
  getAssetInventoryOwnClaims(params: { clientId: string }): Promise<AssetOwnClaimsTab>;
  saveAssetInventoryOwnClaims(params: {
    clientId: string;
    saveAssetOwnClaimsRequest: SaveAssetOwnClaims;
  }): Promise<AssetOwnClaimsTab>;
  getAssetInventoryAssets(params: { clientId: string }): Promise<AssetsTab>;
  saveAssetInventoryAssets(params: { clientId: string; saveAssetsRequest: SaveAssets }): Promise<AssetsTab>;
  getAssetInventoryPropertyRights(params: { clientId: string }): Promise<PropertyRightsTab>;
  saveAssetInventoryPropertyRights(params: {
    clientId: string;
    savePropertyRightsRequest: SavePropertyRights;
  }): Promise<PropertyRightsTab>;
  getAssetInventoryDonations(params: { clientId: string }): Promise<DonationsTab>;
  saveAssetInventoryDonations(params: { clientId: string; saveDonationsRequest: SaveDonations }): Promise<DonationsTab>;
  getAssetInventoryCompleteness(params: { clientId: string }): Promise<AssetInventoryCompleteness>;
}

/**
 * The asset-inventory document endpoints (bank statements, dependant documents, income and expense proofs, own-claim
 * documents, the upload areas of the assets, other property rights and donations tabs), a slice of their
 * own so the portal can enable the asset-inventory forms before its backend surface exposes the
 * document endpoints. The uploads take the file as a multipart body via the `initOverrides` request
 * overrides (matching the generated clients' fetch signature).
 */
export interface AssetInventoryDocumentsSliceApi {
  uploadAssetInventoryBankStatement(params: { clientId: string }, initOverrides?: RequestInit): Promise<BankStatement>;
  getAssetInventoryBankStatements(params: { clientId: string }): Promise<BankStatement[]>;
  // Optional: the read-toggle endpoint is staff-only (not exposed to the portal), so the external
  // client omits it. Only ever called behind the `markBankStatementsRead` capability (internal-only).
  markAssetInventoryDocumentRead?(params: {
    clientId: string;
    documentId: string;
    markAssetInventoryDocumentReadRequest: MarkAssetInventoryDocumentRead;
  }): Promise<void>;
  deleteAssetInventoryBankStatement(params: { clientId: string; documentId: string }): Promise<void>;
  uploadAssetInventoryDependantDocument(
    params: { clientId: string },
    initOverrides?: RequestInit
  ): Promise<ClientDocument>;
  getAssetInventoryDependantDocuments(params: { clientId: string }): Promise<ClientDocument[]>;
  deleteAssetInventoryDependantDocument(params: { clientId: string; documentId: string }): Promise<void>;
  uploadAssetInventoryIncomeProof(params: { clientId: string }, initOverrides?: RequestInit): Promise<IncomeProof>;
  getAssetInventoryIncomeProofs(params: { clientId: string }): Promise<IncomeProof[]>;
  deleteAssetInventoryIncomeProof(params: { clientId: string; documentId: string }): Promise<void>;
  uploadAssetInventoryExpenseProof(params: { clientId: string }, initOverrides?: RequestInit): Promise<ExpenseProof>;
  getAssetInventoryExpenseProofs(params: { clientId: string }): Promise<ExpenseProof[]>;
  deleteAssetInventoryExpenseProof(params: { clientId: string; documentId: string }): Promise<void>;
  uploadAssetInventoryOwnClaimDocument(
    params: { clientId: string },
    initOverrides?: RequestInit
  ): Promise<OwnClaimDocument>;
  getAssetInventoryOwnClaimDocuments(params: { clientId: string }): Promise<OwnClaimDocument[]>;
  // Optional: only staff may delete an own-claim document, so the endpoint is not exposed to the portal and
  // the external client omits it. Without it the rows render without the delete action.
  deleteAssetInventoryOwnClaimDocument?(params: { clientId: string; documentId: string }): Promise<void>;
  uploadAssetInventoryAssetDocument(
    params: { clientId: string; area: AssetDocumentArea },
    initOverrides?: RequestInit
  ): Promise<AssetDocument>;
  getAssetInventoryAssetDocuments(params: { clientId: string; area: AssetDocumentArea }): Promise<AssetDocument[]>;
  deleteAssetInventoryAssetDocument(params: {
    clientId: string;
    area: AssetDocumentArea;
    documentId: string;
  }): Promise<void>;
}

/**
 * Optional document drag-and-drop between the app's dropzone panel and the asset-inventory upload
 * areas. Internal-app only: the portal has no dropzone panel and omits the slice, and the shared
 * document blocks fall back to a plain upload area. The injected component must be module-scope
 * (stable identity) so React does not remount the section subtree on re-render.
 */
export interface DocumentDndSliceApi {
  /** The full upload-area section (drop target + rows with file actions + pending moves). */
  AssetDocumentSection: ComponentType<AssetDocumentSectionProps>;
  /** Persists one queued move. Throws on failure; the caller reports errors per file. */
  moveDocument(params: { clientId: string; target: string; documentId: string }): Promise<unknown>;
  /** Invalidates every document list a cross-zone move can affect (panel sections + asset areas). */
  invalidateDocumentZoneQueries(queryClient: QueryClient, clientId: string): void;
}

/**
 * The claims (Verbindlichkeiten) endpoints. Both apps' generated `ClaimsApi` satisfies this
 * structurally (the internal adapter maps its singleton directly). The mutating calls accept the
 * generated clients' optional `initOverrides` second argument.
 */
export interface ClaimsSliceApi {
  getClaimsByClientId(params: { clientId: string }): Promise<Claim[]>;
  getClaimById(params: { clientId: string; claimId: string }): Promise<Claim>;
  createClaim(
    params: { clientId: string; createClaimRequest: CreateClaim },
    initOverrides?: RequestInit
  ): Promise<Claim>;
  updateClaim(
    params: { clientId: string; claimId: string; updateClaimRequest: UpdateClaim },
    initOverrides?: RequestInit | ApiInitOverride
  ): Promise<Claim>;
  deleteClaim(params: { clientId: string; claimId: string }, initOverrides?: RequestInit): Promise<void>;
  /**
   * Optional: the bulk surcharge is staff-only and therefore absent from the portal-exposed
   * endpoint subset, so the portal's generated client cannot satisfy it. The toolbar button
   * presence-gates itself on this method.
   */
  applyClaimBalanceSurcharge?(
    params: { clientId: string; applyClaimBalanceSurchargeRequest: ApplyClaimBalanceSurcharge },
    initOverrides?: RequestInit | ApiInitOverride
  ): Promise<ClaimBalanceSurcharge>;
  /**
   * Optional for the same reason as the bulk surcharge above: staff-only, so the portal's generated
   * client does not carry it and the toolbar button presence-gates itself on this method.
   */
  carryForwardClaimBalances?(
    params: { clientId: string; carryForwardClaimBalancesRequest: CarryForwardClaimBalances },
    initOverrides?: RequestInit | ApiInitOverride
  ): Promise<ClaimBalanceCarryForward>;
}

/**
 * The proceeding-participant (Verfahrensbeteiligte) endpoints the claim and asset-inventory areas
 * need. The method names/params are kept neutral so BOTH apps satisfy them from their own generated
 * client: the internal adapter maps `searchParticipants` to the unscoped participant search (passing
 * `clientId` through as the optional scope) and `getParticipantById` to the global-by-id route
 * (ignoring `clientId`); the portal adapter maps them to its client-scoped participant routes.
 * `clientId` is therefore always supplied by the shared code.
 */
export interface ParticipantsSliceApi {
  // `clientId` is optional: internally an omitted clientId means "search all participants" (the
  // query param is dropped), while the portal always supplies its own client. `types` restricts the
  // results to the given participant types (e.g. [Bank] for the salary-account bank picker).
  searchParticipants(params: {
    clientId?: string;
    searchText?: string;
    includeInactive?: boolean;
    types?: ProceedingParticipantType[];
  }): Promise<ProceedingParticipantSearchResults>;
  // `clientId` is carried so the portal adapter can hit its client-scoped by-id route; the internal
  // adapter ignores it and fetches by participant id alone.
  getParticipantById(params: { clientId?: string; participantId: string }): Promise<ProceedingParticipant>;
  createClientSpecificParticipant(params: {
    clientId: string;
    createClientSpecificProceedingParticipantRequest: CreateClientSpecificProceedingParticipant;
  }): Promise<ProceedingParticipant>;
  updateClientSpecificParticipant(params: {
    clientId: string;
    participantId: string;
    updateProceedingParticipantRequest: UpdateProceedingParticipant;
  }): Promise<ProceedingParticipant>;
}

export interface ClientNoteSliceApi {
  getClientNote(params: { clientId: string }, initOverrides?: RequestInit | ApiInitOverride): Promise<ClientNote>;
  updateClientNote(params: { clientId: string; updateClientNoteRequest: UpdateClientNote }): Promise<ClientNote>;
}

/**
 * The per-area API slices the shared client-file code calls. Extended alongside each lifted area.
 */
export interface ClientFileApi {
  bankruptcyChecklist?: BankruptcyChecklistSliceApi;
  assetInventory?: AssetInventorySliceApi;
  assetInventoryDocuments?: AssetInventoryDocumentsSliceApi;
  documentDnd?: DocumentDndSliceApi;
  lookups?: LookupsSliceApi;
  claims?: ClaimsSliceApi;
  participants?: ParticipantsSliceApi;
  clientNote?: ClientNoteSliceApi;
}
